/**
 * Maps Soroban contract error codes to short messages (aligned with
 * server/v2/lib/soroban.js `V2_ERROR_MESSAGES`). Used when the API still
 * returns raw simulation text or for client-side safety.
 */

const CONTRACT_ERROR_MESSAGES: Record<number, string> = {
  1: 'Not initialized',
  2: 'Unauthorized',
  3: 'Country not registered',
  4: 'Country already registered',
  5: 'Invalid numeric country code',
  6: 'Contract registry not set',
  100: 'Country vault disabled',
  101: 'Insufficient balance',
  102: 'Balance overflow',
  103: 'Balance underflow',
  104: 'Invalid amount',
  105: 'Transfer to same country',
  106: 'Transfer not allowed',
  107: 'Compliance check failed',
  108: 'Daily limit exceeded',
  109: 'Monthly limit exceeded',
  110: 'Transaction amount too small',
  111: 'Transaction amount too large',
  112: 'WebAuthn signature required',
  113: 'WebAuthn signature invalid',
  200: 'Policy not found',
  201: 'Country restricted',
  202: 'Sanctions block',
  203: 'KYC verification required',
  204: 'KYC rejected',
  205: 'User restricted',
  206: 'Deposits disabled for this country',
  207: 'Withdrawals disabled for this country',
  208: 'Transfers in disabled for this country',
  209: 'Transfers out disabled for this country',
  300: 'Session not found',
  301: 'Session not in waiting state',
  302: 'Session not active',
  303: 'Session is full',
  304: 'Player already in session',
  305: 'Not a trust group member',
  306: 'ZK proof required for this session',
  307: 'ZK proof invalid',
  308: 'Event already anchored',
  400: 'VK not set',
  401: 'VK not found',
  402: 'Proof replay detected',
  403: 'Proof too old',
  404: 'Proof malformed',
  405: 'Public inputs mismatch',
  406: 'Pairing verification failed',
  407: 'Unsupported proof type',
  408: 'Unsupported curve',
  409: 'Context mismatch',
  410: 'Batch size too large',
  6001: 'Contract already initialized',
  6002: 'Contract not initialized',
  6003: 'Unauthorized — must be operator or super_admin',
  6004: 'Proposal receipt already exists',
  6005: 'Proposal receipt not found',
  6006: 'Invalid status for this operation',
  6007: 'Escrow already exists',
  6008: 'Escrow not found',
  6009: 'Escrow already settled',
  6010: 'Amount must be greater than zero',
  6011: 'Funder must be treasury_admin or super_admin',
  11001: 'Agent registry already initialized',
  11002: 'Agent registry not initialized',
  11003: 'Unauthorized — must be the agent operator',
  11101: 'Agent already registered',
  11102: 'Agent not found',
  11103: 'Too many capabilities (max 32)',
  11104: 'Agent has been revoked',
  12001: 'Agent policy already initialized',
  12002: 'Agent policy not initialized',
  12003: 'Unauthorized — must be the agent operator',
  12101: 'Agent has not been bound to an operator',
  12102: 'Agent already bound to an operator',
  12103: 'Operator does not match the binding',
  12104: 'Amount must not be negative',
  12105: 'Agent policy has expired',
  12106: 'Contract not on the agent allow-list',
  12107: 'Call exceeds the agent spending cap',
  13001: 'Agent position contract already initialized',
  13002: 'Agent position contract not initialized',
  13003: 'Unauthorized — must be the position-tier admin',
  13101: 'Stale broadcast_seq — write is older than the last checkpoint',
  13102: 'Latitude must be within ±90 degrees',
  13103: 'Longitude must be within ±180 degrees',
  14001: 'Citizenship contract already initialized',
  14002: 'Citizenship contract not initialized',
  14003: 'Unauthorized — must be the agent or admin for this action',
  14101: 'No citizenship membership found for this (agent, country)',
  14102: 'Invalid state transition for the current citizenship status',
  14103: 'Agent is already an active resident of another country',
  14104: 'Paid amount must be greater than zero',
  14105: 'Expiry must be in the future',
  14106: 'policy_version must be ≥ 1',
};

function parseContractErrorCode(raw: string): number {
  const sorobanMatch = raw.match(/Error\s*\(\s*Contract\s*,\s*#(\d+)\s*\)/i);
  if (sorobanMatch) return parseInt(sorobanMatch[1], 10);
  const lower = raw.toLowerCase();
  const fallback =
    lower.match(/error\s+code\s*[:\s]+(\d+)/i) ||
    lower.match(/\bwasmerror\b.*?(\d+)/) ||
    lower.match(/contracterror.*?(\d+)/) ||
    lower.match(/hostvalerror.*?(\d+)/);
  if (fallback) return parseInt(fallback[1], 10);
  return -1;
}

/** Strip Soroban diagnostic/event dumps so UI stays readable. */
function stripVerboseSimulationTail(raw: string): string {
  const idx = raw.search(/\n\s*Event log\s*\(/i);
  if (idx >= 0) return raw.slice(0, idx).trim();
  const idx2 = raw.search(/\n0:\s*\[Diagnostic Event\]/i);
  if (idx2 >= 0) return raw.slice(0, idx2).trim();
  return raw.trim();
}

export interface DecodedContractError {
  code: number;
  message: string;
}

export function decodeContractErrorMessage(raw: string): DecodedContractError {
  const cleaned = stripVerboseSimulationTail(raw);
  const code = parseContractErrorCode(cleaned);
  if (code >= 0) {
    const base = CONTRACT_ERROR_MESSAGES[code] || `Contract error ${code}`;
    return { code, message: base };
  }
  return { code: -1, message: cleaned || 'Unknown error' };
}

/** Short hint after the main message for common registry cases. */
function hintForCode(code: number): string | null {
  if (code === 11101) {
    return 'Try a different agent address, or change capabilities with set_capabilities if this agent is already yours.';
  }
  if (code === 11104) {
    return 'This agent was revoked; you may need to register a new agent address.';
  }
  return null;
}

/**
 * Turn raw Soroban simulation / RPC errors into a short, readable line.
 * Passes through text that does not look like a contract error.
 */
export function humanizeSorobanError(raw: string): string {
  if (!raw || !raw.trim()) return 'Something went wrong';
  const cleaned = stripVerboseSimulationTail(raw);
  const decoded = decodeContractErrorMessage(cleaned);
  if (decoded.code >= 0) {
    const hint = hintForCode(decoded.code);
    return hint ? `${decoded.message}. ${hint}` : decoded.message;
  }
  // Short HTTP-style messages: leave as-is if already human
  if (cleaned.length < 200 && !cleaned.includes('HostError')) return cleaned;
  return cleaned;
}

export function humanizeUnknownError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  return humanizeSorobanError(raw);
}
